import React, { useState, useEffect, useRef } from "react";
import AsyncSelect from "react-select/async"; 
import { customFetch } from '../apiClient';
import '../styles/main.css';

const initialFetched = {
  customer: { custName: "", mobileNo: "", cAddress: "", age: "" },
  summary: {
    total: 0, dis: 0, received: 0, cash: 0, upi: 0, due: 0,
    billDate: null, disAuthority: "", requestedBy: "",
  },
  details: [],
};

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: '34px',
    fontSize: '12px',
    borderRadius: '6px',
    borderColor: state.isFocused ? 'var(--theme-color, #0088cc)' : '#cbd5e0',
    boxShadow: state.isFocused ? '0 0 0 3px color-mix(in srgb, var(--theme-color, #0088cc), transparent 85%)' : 'none',
    '&:hover': { borderColor: 'var(--theme-color, #0088cc)' },
    borderWidth: '1.5px'
  }),
  menu: (base) => ({ ...base, zIndex: 9999, borderRadius: '6px' }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  option: (base, state) => ({
    ...base,
    fontSize: '12px',
    padding: '6px 10px',
    backgroundColor: state.isSelected 
      ? 'var(--theme-color, #0088cc)' 
      : state.isFocused 
        ? 'color-mix(in srgb, var(--theme-color, #0088cc), white 90%)' 
        : 'white',
    color: state.isSelected ? 'white' : '#333',
    cursor: 'pointer',
    '&:active': { backgroundColor: 'var(--theme-color, #0088cc)', color: 'white' }
  }),
  placeholder: (base) => ({ ...base, color: '#a0aec0', fontSize: '12px' })
};

const Billpopup = ({ billNo, onClose, onUpdateSuccess }) => {
  const [fetched, setFetched] = useState(initialFetched);
  
  // Payment States
  const [discountValue, setDiscountValue] = useState(""); 
  const [discountType, setDiscountType] = useState("amount"); 
  const [cash, setCash] = useState("");
  const [upi, setUpi] = useState("");

  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState("");
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const token = sessionStorage.getItem("token");

  // New states for add service item feature
  const [showAddService, setShowAddService] = useState(false);
  const [serviceForm, setServiceForm] = useState({
    services: "", qty: 1, rate: 0, amount: 0,
  });

  // Refs for Navigation
  const formRef = useRef(null);
  const serviceSelectWrapperRef = useRef(null);

  // --- Dynamic Calculation Logic ---
  const totalAmount = fetched.details.reduce(
    (sum, d) => sum + Number(d.amount ?? d.AMOUNT ?? 0), 0
  );

  const calculateDiscountAmount = () => {
      const val = Number(discountValue) || 0;
      if (discountType === "percent") {
          return (totalAmount * val) / 100;
      }
      return val;
  };

  const finalDiscountAmount = calculateDiscountAmount();
  const payable = totalAmount - finalDiscountAmount;
  const received = (Number(cash) || 0) + (Number(upi) || 0);
  const due = payable - received;

  // --- Load Bill Data ---
  useEffect(() => {
    if (!billNo) return;
    const fetchBill = async () => {
      setLoading(true); setNotFound(""); setError(""); setSuccess("");
      try {
     const res = await customFetch(`/Billing/search?billNo=${encodeURIComponent(billNo)}`);
        if (!res.ok) {
          setNotFound(await res.text()); setFetched(initialFetched);
        } else {
          const data = await res.json();
          if (Array.isArray(data) && data.length) {
             const billData = data[0];
             setFetched(billData);
             
             setDiscountType("amount"); 
             setDiscountValue(billData.summary.dis || ""); 
             setCash(billData.summary.cash || "");
             setUpi(billData.summary.upi || "");
             
             // Auto Focus on Load
             setTimeout(() => {
                const firstInput = formRef.current?.querySelector('input:not([disabled])');
                firstInput?.focus();
             }, 300);
          }
          else setNotFound("No bill found");
        }
      } catch (ex) {
        setError("Search error"); setFetched(initialFetched);
      } finally { setLoading(false); }
    };
    fetchBill();
  }, [billNo, token]);

  // --- ⌨️ Keyboard Navigation Logic (Advanced) ---
  const handleGlobalKeyDown = (e) => {
    
    // 1. Handle Service Search Box (AsyncSelect)
    if (e.target.closest('.service-search-container')) {
        const inputVal = e.target.value;
        if (e.key === 'Enter') {
            // ✅ SKIP LOGIC: If empty Enter -> Jump to Discount (Exit Loop)
            if (!inputVal && !serviceForm.services) {
                e.preventDefault();
                setShowAddService(false); // Close panel optionally
                
                // Find Discount Input
                const discountInput = formRef.current.querySelector('input[type="number"]'); // First number input in summary section
                // Better selector specifically for discount
                const allNumberInputs = formRef.current.querySelectorAll('input[type="number"]');
                // Finding the one that holds discountValue might be tricky by value, let's use order or placeholder
                // Since Discount is usually the first editable number input in summary section after table
                // Let's rely on finding input after the table
                
                // Or better: Use name attribute if added (I added name="discountInput" in previous code, let's ensure it's there)
                // Assuming we add name="discountInput" to discount field
                const discountField = formRef.current.querySelector('input[value="' + discountValue + '"]') || allNumberInputs[allNumberInputs.length - 5]; // Fallback
                
                // Let's target the discount input explicitly in JSX with a ref or class
                // I will add a class 'discount-input' to it in JSX below
                const targetDiscount = formRef.current.querySelector('.discount-input');
                
                if (targetDiscount) {
                    targetDiscount.focus();
                    targetDiscount.select();
                }
                return;
            }
            return; 
        }
    }

    const focusable = formRef.current.querySelectorAll(
      'input:not([disabled]):not([readonly]):not([type="hidden"]), ' +
      'select:not([disabled]), ' +
      'button:not([disabled]):not(.btn-close-popup), ' + 
      'textarea:not([disabled])'
    );
    
    if (!focusable.length) return;
    const elementsArr = Array.from(focusable);
    const activeIndex = elementsArr.indexOf(document.activeElement);

    if (e.key === 'Enter') {
        e.preventDefault(); 

        // CASE: "Add Service" Toggle Button -> Open Panel & Focus Search
        if (document.activeElement.classList.contains('btn-small-primary')) {
            setShowAddService(true); // Ensure open
            setTimeout(() => {
                const input = serviceSelectWrapperRef.current?.querySelector('input');
                input?.focus();
            }, 50);
            return;
        }

        // CASE: "Add" Button inside Panel -> Add Item & Loop back to Search
        if (document.activeElement.classList.contains('btn-add')) {
            handleAddServiceItem();
            return;
        }

        // CASE: Update Bill Button
        if (document.activeElement.classList.contains('btn-update')) {
            handleUpdate(e);
            return;
        }

        // Normal Navigation
        const nextIndex = activeIndex + 1;
        if (nextIndex < elementsArr.length) {
            elementsArr[nextIndex].focus();
            if (elementsArr[nextIndex].tagName === 'INPUT') {
                elementsArr[nextIndex].select();
            }
        }
    }
  };

  useEffect(() => {
    const handler = (e) => {
        if (formRef.current && formRef.current.contains(e.target)) {
            handleGlobalKeyDown(e);
        }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [showAddService, serviceForm, discountValue]); // Dependencies updated

  // Handlers
  const updateCustomer = (e) =>
    setFetched((f) => ({ ...f, customer: { ...f.customer, [e.target.name]: e.target.value } }));
  
  const updateSummaryMeta = (e) =>
    setFetched((f) => ({ ...f, summary: { ...f.summary, [e.target.name]: e.target.value } }));
  
  const updateDetail = (idx, field, val) => {
    const details = [...fetched.details];
    let value = val;
    if (["qty", "rate", "QTY", "RATE"].includes(field)) value = Number(val);
    
    details[idx] = { ...details[idx], [field]: value };
    if (["qty", "rate", "QTY", "RATE"].includes(field)) {
      const qty = Number(details[idx].qty ?? details[idx].QTY ?? 0);
      const rate = Number(details[idx].rate ?? details[idx].RATE ?? 0);
      details[idx].amount = +(qty * rate).toFixed(2);
      details[idx].AMOUNT = details[idx].amount;
    }
    setFetched((f) => ({ ...f, details }));
  };

 // --- API Loader (Modified for Strict Billing Filter) ---
const loadServiceOptions = async (inputValue) => {
    try {
      const res = await customFetch(`/ChargeMaster/list?search=${encodeURIComponent(inputValue)}&type=BILLING`);
        
        if (!res.ok) throw new Error("Failed to fetch");
        const data = await res.json();
        
        // Backend 'type' filter ke baad frontend map
        return data.map((srv) => ({
            label: srv.chargeName || "(No Name)", 
            value: srv.srno, 
            amount: srv.amount, 
            unit: srv.unit,
        }));
    } catch (err) { 
        console.error("Popup Service Load Error:", err);
        return []; 
    }
};
  const handleServiceChange = (selectedOption) => {
    if (!selectedOption) {
      setServiceForm({ services: "", qty: 1, rate: 0, amount: 0 }); return;
    }
    setServiceForm({
      services: selectedOption.label, qty: 1, rate: selectedOption.amount || 0,
      amount: (selectedOption.amount || 0).toFixed(2),
    });
    // Focus Qty
    setTimeout(() => {
        const qtyInput = formRef.current.querySelector('.add-service-panel input[type="number"]');
        qtyInput?.focus();
        qtyInput?.select();
    }, 100);
  };

  useEffect(() => {
    const qtyNum = Number(serviceForm.qty) || 0;
    const rateNum = Number(serviceForm.rate) || 0;
    setServiceForm((prev) => ({ ...prev, amount: (qtyNum * rateNum).toFixed(2) }));
  }, [serviceForm.qty, serviceForm.rate]);

  const handleAddServiceItem = () => {
    if (!serviceForm.services) { setError("Please select a service."); return; }
    if (!serviceForm.qty || Number(serviceForm.qty) <= 0) { setError("Quantity > 0 required."); return; }
    setError(""); setSuccess("");

    const newService = {
      services: serviceForm.services, qty: Number(serviceForm.qty),
      rate: Number(serviceForm.rate), amount: Number(serviceForm.amount),
    };

    setFetched((prev) => ({ ...prev, details: [...prev.details, newService] }));
    setServiceForm({ services: "", qty: 1, rate: 0, amount: 0 });
    
    // ✅ Logic: Keep Panel Open & Focus Search (Looping)
    setShowAddService(true);
    setTimeout(() => {
        const input = serviceSelectWrapperRef.current?.querySelector('input');
        input?.focus();
    }, 50);
  };

  const handleUpdate = async (e) => {
    e.preventDefault(); setLoading(true); setSuccess(""); setError("");
    
    try {
      const payload = {
        Customer: fetched.customer,
        Summary: {
          ...fetched.summary, 
          total: totalAmount, 
          ipdId: fetched.summary.ipdId,
          dis: finalDiscountAmount,
          payable: payable,
          due: due, 
          received: received,
          cash: Number(cash) || 0,
          upi: Number(upi) || 0,
        },
        Details: fetched.details.map((d) => ({
          ...d, qty: d.qty ?? d.QTY, rate: d.rate ?? d.RATE, amount: d.amount ?? d.AMOUNT,
        })),
      };

    const res = await customFetch(`/BillingTransaction/UpdateBilling/${billNo}`, {
    method: "PUT", 
    body: JSON.stringify(payload),
});
      if (!res.ok) throw new Error((await res.text()) || "Update failed");
      setSuccess("Bill updated successfully!");
      if (onUpdateSuccess) onUpdateSuccess();
    } catch (err) { setError(err.message || "Update error"); } finally { setLoading(false); }
  };

  const handleRemoveDetail = (index) => {
    setFetched((f) => ({ ...f, details: f.details.filter((_, i) => i !== index) }));
  };

  return (
    <div className="popup-overlay" onClick={onClose}>
      <div 
        className="popup-container" 
        onClick={(e) => e.stopPropagation()}
      >
        <div className="popup-header">
          <h5>Update Bill: {billNo}</h5>
          <button className="btn-close-popup" onClick={onClose}>&times;</button>
        </div>

        <div className="popup-body">
          {loading && <div className="text-center p-3 text-muted">Loading...</div>}
          {notFound && <div className="alert-custom alert-warning">{notFound}</div>}
          {error && <div className="alert-custom alert-danger">{error}</div>}
          {success && <div className="alert-custom alert-success">{success}</div>}

          {!!fetched?.summary?.billNo && (
            <form ref={formRef} onSubmit={handleUpdate}>
              
              {/* Customer Section */}
              <div className="form-section">
                <h6 className="section-header">Customer Details</h6>
                <div className="row-grid four-col">
                  <div className="form-group">
                    <label>Name</label>
                    <input type="text" name="custName" className="form-input" value={fetched.customer.custName || ""} onChange={updateCustomer} />
                  </div>
                  <div className="form-group">
                    <label>Mobile</label>
                    <input type="text" name="mobileNo" className="form-input" value={fetched.customer.mobileNo || ""} onChange={updateCustomer} />
                  </div>
                  <div className="form-group">
                    <label>Age</label>
                    <input type="number" name="age" className="form-input" value={fetched.customer.age || ""} onChange={updateCustomer} min={0} />
                  </div>
                  <div className="form-group">
                    <label>Address</label>
                    <input type="text" name="cAddress" className="form-input" value={fetched.customer.cAddress || ""} onChange={updateCustomer} />
                  </div>
                </div>
              </div>

              {/* Items Section */}
              <div className="form-section mt-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                   <h6 className="section-header mb-0">Billing Items</h6>
                   <button type="button" className="btn-small-primary" onClick={() => {
                       setShowAddService(!showAddService);
                       setTimeout(() => {
                           if(!showAddService) { 
                               const input = serviceSelectWrapperRef.current?.querySelector('input');
                               input?.focus();
                           }
                       }, 100);
                   }}>
                     {showAddService ? "Cancel" : "+ Add Service"}
                   </button>
                </div>

                {/* Add Service Panel */}
                {showAddService && (
                  <div className="add-service-panel">
                    <div className="form-group mb-2 service-search-container" ref={serviceSelectWrapperRef}>
                       <label>Select Service</label>
                       <AsyncSelect cacheOptions loadOptions={loadServiceOptions} onChange={handleServiceChange} placeholder="Search..." isClearable styles={selectStyles} value={serviceForm.services ? { label: serviceForm.services, value: null } : null} />
                    </div>
                    <div className="row-grid four-col">
                      <input type="number" className="form-input" placeholder="Qty" value={serviceForm.qty} onChange={(e) => setServiceForm(p => ({...p, qty: e.target.value}))} min={1} />
                      <input type="number" className="form-input" placeholder="Rate" value={serviceForm.rate} onChange={(e) => setServiceForm(p => ({...p, rate: e.target.value}))} min={0} />
                      <input type="number" className="form-input readonly" placeholder="Amt" value={serviceForm.amount} readOnly />
                      <button type="button" className="btn-add" disabled={!serviceForm.services} onClick={handleAddServiceItem}>Add</button>
                    </div>
                  </div>
                )}

                <div className="table-responsive">
                  <table className="custom-table">
                    <thead>
                      <tr><th>Service</th><th width="70">Qty</th><th width="90">Rate</th><th width="90">Amount</th><th width="40"></th></tr>
                    </thead>
                    <tbody>
                      {fetched.details.map((dt, idx) => (
                        <tr key={idx}>
                          <td><input className="table-input readonly" value={dt.services ?? dt.Services} readOnly /></td>
                          <td><input type="number" className="table-input" min={1} value={dt.qty ?? dt.QTY} onChange={(e) => updateDetail(idx, "qty", e.target.value)} /></td>
                          <td><input type="number" className="table-input" min={0} step="0.01" value={dt.rate ?? dt.RATE} onChange={(e) => updateDetail(idx, "rate", e.target.value)} /></td>
                          <td><input type="number" className="table-input readonly" value={dt.amount ?? dt.AMOUNT} readOnly /></td>
                          <td className="text-center"><i className="bi bi-trash text-danger cursor-pointer" onClick={() => handleRemoveDetail(idx)}></i></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Summary Section */}
              <div className="form-section mt-3 summary-bg">
                <div className="row-grid four-col">
                   <div className="form-group"><label>Total</label><input className="form-input readonly fw-bold" value={Number(totalAmount).toFixed(2)} readOnly /></div>
                   
                   <div className="form-group">
                        <label>Discount</label>
                        <div className="d-flex">
                            <select 
                                className="form-input" 
                                style={{width: '60px', borderRight: 'none', borderRadius: '4px 0 0 4px', padding: '4px'}}
                                value={discountType}
                                onChange={(e) => setDiscountType(e.target.value)}
                            >
                                
                                <option value="amount">₹</option>
                                <option value="percent">%</option>
                            </select>
                            
                            {/* ✅ Added Class 'discount-input' for Keyboard Navigation */}
                            <input 
                                type="number" 
                                min="0" 
                                className="form-input discount-input" 
                                style={{borderRadius: '0 4px 4px 0', flex: 1}}
                                value={discountValue} 
                                onChange={(e) => setDiscountValue(e.target.value)} 
                                placeholder="0" 
                            />
                        </div>
                   </div>

                   <div className="form-group"><label>Payable</label><input className="form-input readonly fw-bold text-success" value={payable.toFixed(2)} readOnly /></div>
                   <div className="form-group"><label>Due</label><input className="form-input readonly fw-bold text-danger" value={due.toFixed(2)} readOnly /></div>
                </div>
                <div className="row-grid four-col mt-2">
                   <div className="form-group"><label>Cash</label><input type="number" className="form-input" min={0} value={cash} onChange={(e) => setCash(e.target.value)} /></div>
                   <div className="form-group"><label>UPI</label><input type="number" className="form-input" min={0} value={upi} onChange={(e) => setUpi(e.target.value)} /></div>
                   <div className="form-group"><label>Authority<span className="text-danger">*</span></label><input className="form-input" value={fetched.summary.disAuthority || ""} onChange={updateSummaryMeta} name="disAuthority" /></div>
                   <div className="form-group"><label>Requested By</label><input className="form-input" value={fetched.summary.requestedBy || ""} onChange={updateSummaryMeta} name="requestedBy" /></div>
                </div>
              </div>

              <div className="popup-footer mt-3">
                <button type="submit" className="btn-update" disabled={loading}>{loading ? "Updating..." : "Update Bill"}</button>
              </div>

            </form>
          )}
        </div>
      </div>

      <style jsx>{`
        .popup-overlay {
          position: fixed; top: 0; left: 0; right: 0; bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          backdrop-filter: blur(2px);
          z-index: 9998;
        }
        
        .popup-container {
          position: fixed;
          top: 4%;
          left: 50%;
          transform: translateX(-50%);
          width: 900px; max-width: 95vw; max-height: 90vh;
          background: white; padding: 0; border-radius: 8px;
          box-shadow: 0 10px 40px rgba(0,0,0,0.25); border: 1px solid #ddd;
          display: flex; flex-direction: column; overflow: hidden;
          z-index: 9999;
        }

        .popup-header {
          background: var(--theme-color, #0088cc);
          padding: 12px 16px; display: flex; justify-content: space-between; align-items: center;
          color: white; border-bottom: 2px solid rgba(0,0,0,0.1);
        }
        .popup-header h5 { margin: 0; font-size: 16px; font-weight: 600; }
        
        .btn-close-popup {
          background: rgba(255,255,255,0.2); border: none; color: white;
          font-size: 20px; width: 30px; height: 30px; border-radius: 4px;
          cursor: pointer; display: flex; align-items: center; justify-content: center;
        }
        .btn-close-popup:hover { background: rgba(255,255,255,0.4); }

        .popup-body { padding: 16px; overflow-y: auto; flex: 1; }

        .section-header {
          font-size: 13px; font-weight: 700; color: var(--theme-color, #0088cc);
          margin-bottom: 8px; text-transform: uppercase; letter-spacing: 0.5px;
        }

        .row-grid { display: grid; gap: 10px; }
        .four-col { grid-template-columns: repeat(4, 1fr); }

        .form-group { display: flex; flex-direction: column; }
        .form-group label { font-size: 11px; font-weight: 600; color: #555; margin-bottom: 3px; }
        
        .form-input {
          padding: 6px 10px; border: 1px solid #ccc; border-radius: 4px;
          font-size: 12px; outline: none; width: 100%; transition: all 0.2s;
        }
        .form-input:focus {
          border-color: var(--theme-color, #0088cc);
          box-shadow: 0 0 0 2px color-mix(in srgb, var(--theme-color), transparent 90%);
        }
        .readonly { background: #f9f9f9; color: #777; cursor: default; }

        .custom-table { width: 100%; border-collapse: collapse; margin-top: 5px; font-size: 12px; }
        .custom-table thead { background: var(--theme-color, #0088cc); }
        .custom-table th {
          background: transparent; padding: 8px 10px; text-align: left;
          font-weight: 600; color: white; border: none;
        }
        .custom-table td { padding: 4px; border-bottom: 1px solid #eee; vertical-align: middle; }

        .table-input {
          width: 100%; border: 1px solid transparent; background: transparent; 
          padding: 4px 6px; font-size: 12px; outline: none; color: inherit;
        }
        .table-input:focus {
          background: white; border-color: var(--theme-color, #0088cc);
          border-radius: 4px; box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.05);
        }
        .table-input.readonly { color: #666; cursor: default; }

        .add-service-panel {
          background: #f0f7ff; padding: 12px; border-radius: 6px;
          border: 1px solid #dbeafe; margin-bottom: 10px;
        }

        .btn-small-primary {
          background: var(--theme-color, #0088cc); color: white; border: none;
          padding: 4px 12px; font-size: 11px; border-radius: 4px; cursor: pointer;
        }
        .btn-add {
          background: #27ae60; color: white; border: none;
          padding: 6px; font-size: 12px; border-radius: 4px; cursor: pointer; width: 100%;
        }
        
        .btn-update { 
          width: 100%; padding: 10px; border: none; border-radius: 6px; 
          background: #27ae60; color: white; font-weight: 600; font-size: 14px; cursor: pointer; 
          transition: 0.2s;
        }
        .btn-update:hover { background: #219150; transform: translateY(-1px); }

        .text-danger { color: #e74c3c !important; }
        .text-success { color: #27ae60 !important; }
        .fw-bold { font-weight: 700; }
        .cursor-pointer { cursor: pointer; }

        .alert-custom { padding: 8px; border-radius: 4px; font-size: 12px; margin-bottom: 10px; }
        .alert-warning { background: #fff3cd; color: #856404; border: 1px solid #ffeeba; }
        .alert-danger { background: #f8d7da; color: #721c24; border: 1px solid #f5c6cb; }
        .alert-success { background: #d4edda; color: #155724; border: 1px solid #c3e6cb; }

        @media (max-width: 768px) {
          .popup-container { width: 95vw; height: 90vh; top: 5% !important; left: 2.5% !important; }
          .four-col { grid-template-columns: 1fr 1fr; }
        }
      `}</style>
    </div>
  );
};

export default Billpopup;